import { moduleLabel, formatDate, TYPE_LABEL } from '../format.js';
import { LIFECYCLE_HELP, isRejected } from '../lifecycle.js';
import LifecycleControl, { LifecycleBadge } from './LifecycleControl.jsx';
import { navigate, taskPath } from '../OrbitDevelopmentApp.jsx';

/**
 * The working views' table. Reads like the Pipeline and behaves nothing like it.
 *
 * Same columns, same widths, same one-line rows — someone moving between the tabs should not
 * have to re-learn where anything is. What is deliberately absent is every way of REARRANGING
 * it: no grip, no drag, no sort, no rename, no New task. These groups are not filing somebody
 * chose, they are the module column read back, so there is nothing here to put in an order.
 *
 * Module is a group header rather than a column, which is the one place the shape differs from
 * the Pipeline: printing the same module name down forty rows is a column that says nothing.
 */
// No Module column: both views that use this table group BY module, so it would repeat its own
// group header down every row. The QA Queue nests the module groups inside an environment
// heading rather than adding a column for either.
const COLUMNS = [
  // The checkbox column is always present, so the columns line up whether or not a view offers
  // selection — a table whose columns shift by 30px between tabs reads as two different tables.
  { key: 'pick',    label: '',        width: 30 },
  { key: 'task',    label: 'Task',    width: null },
  { key: 'status',  label: 'Status',  width: 158 },
  { key: 'type',    label: 'Type',    width: 104 },
  { key: 'created', label: 'Created', width: 112 },
  { key: 'creator', label: 'Created by', width: 152 },
  { key: 'actions', label: '',        width: 260 },
];

function Head({ selectable, allPicked, somePicked, onToggleAll }) {
  return (
    <table className="o-tbl o-tbl-tight" style={{ marginBottom: -1 }}>
      <colgroup>
        {COLUMNS.map(c => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
      </colgroup>
      <thead>
        <tr>
          {COLUMNS.map(c => (
            <th key={c.key}>
              {c.key === 'pick' && selectable ? (
                <input
                  type="checkbox"
                  aria-label={allPicked ? 'Clear selection' : 'Select every task'}
                  title={allPicked ? 'Clear selection' : 'Select every task'}
                  checked={allPicked}
                  // Neither on nor off: some are picked. Without this the box reads as "none
                  // selected" while three rows below it are ticked.
                  ref={el => { if (el) el.indeterminate = somePicked && !allPicked; }}
                  onChange={onToggleAll}
                  style={{ accentColor: 'var(--ink)', cursor: 'pointer' }}
                />
              ) : c.label}
            </th>
          ))}
        </tr>
      </thead>
    </table>
  );
}

function Row({ task, options, onTransition, saving, selectable, picked, onPick }) {
  const rejected = isRejected(task);

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => navigate(taskPath(task.id))}>
        <td onClick={e => e.stopPropagation()}>
          {selectable && (
            <input
              type="checkbox"
              aria-label={`Include ${task.title} in the export`}
              checked={picked}
              onChange={() => onPick(task.id)}
              style={{ accentColor: 'var(--ink)', cursor: 'pointer' }}
            />
          )}
        </td>
        <td title={rejected && task.last_rejection_note
          ? `${task.title}\n\nSent back: ${task.last_rejection_note}`
          : task.title}>
          <span className="o-clip">
            {/* The one mark that is not a column: a task QA has sent back is the reason this
                view is read first thing in the morning, and it has to survive being one row
                among forty. */}
            {rejected && <span style={{ color: 'var(--error)', marginRight: 6 }}>&#9646;</span>}
            <span className="o-strong">{task.title}</span>
            {task.area && (
              <span className="o-note" style={{ marginLeft: 8, marginBottom: 0 }}>{task.area}</span>
            )}
            {/* The note rides on the same line as the title, clipping like the area does. It
                had a row of its own and that made a task QA rejected twice as tall as one it
                did not — the row height has to be the row height whatever happened to a task,
                so the full text lives on the hover and on the task page. */}
            {rejected && task.last_rejection_note && (
              <span className="o-note"
                style={{ marginLeft: 8, marginBottom: 0, color: 'var(--error)' }}>
                sent back — {task.last_rejection_note}
              </span>
            )}
          </span>
        </td>
        <td title={LIFECYCLE_HELP[task.lifecycle_status]}>
          <LifecycleBadge status={task.lifecycle_status} />
          {Number(task.rejection_count) > 0 && (
            <span className="o-num" style={{ marginLeft: 6, color: 'var(--error)' }}
              title={`Sent back by QA ${task.rejection_count} time`
                + `${Number(task.rejection_count) === 1 ? '' : 's'}`}>
              ×{task.rejection_count}
            </span>
          )}
        </td>
        <td>{TYPE_LABEL[task.task_type] || task.task_type}</td>
        <td>{formatDate(task.logged_at || task.created_at)}</td>
        <td title={task.created_by_name || 'Unknown creator'}>
          <span className="o-clip">{task.created_by_name || 'Unknown creator'}</span>
        </td>
        <td className="o-acts">
          <LifecycleControl task={task} options={options} onTransition={onTransition}
            saving={saving} compact />
        </td>
      </tr>
    </>
  );
}

export default function TaskTable({
  groups, options, onTransition, savingIds, empty,
  selectable = false, picked, onPick, onPickAll,
}) {
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);
  if (total === 0) return <div className="o-panel"><div className="o-state">{empty}</div></div>;

  const ids = groups.flatMap(g => g.tasks.map(t => t.id));
  const pickedHere = ids.filter(id => picked?.has(id)).length;

  return (
    <>
      <Head
        selectable={selectable}
        allPicked={pickedHere > 0 && pickedHere === ids.length}
        somePicked={pickedHere > 0}
        onToggleAll={() => onPickAll(ids, !(pickedHere === ids.length))}
      />
      {groups.filter(g => g.tasks.length > 0).map(group => (
        <section key={group.key} className="o-panel" style={{ padding: 0, marginBottom: 10 }}>
          <header style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
            borderBottom: '1px solid var(--rule-soft)',
          }}>
            <span className="o-strong" style={{ fontWeight: 600 }}>
              {group.label || moduleLabel(options, group.key)}
            </span>
            <span className="o-badge o-grey">{group.tasks.length}</span>
            {group.note && <span className="o-note" style={{ margin: 0 }}>{group.note}</span>}
          </header>
          <div style={{ paddingBottom: 6 }}>
            <table className="o-tbl o-tbl-tight">
              <colgroup>
                {COLUMNS.map(c => (
                  <col key={c.key} style={c.width ? { width: c.width } : undefined} />
                ))}
              </colgroup>
              <tbody>
                {group.tasks.map(t => (
                  <Row key={t.id} task={t} options={options} onTransition={onTransition}
                    saving={!!savingIds?.[t.id]}
                    selectable={selectable} picked={!!picked?.has(t.id)} onPick={onPick} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}
